(ns practitest.frontend.login.views
  (:require
   [practitest.frontend.login.events :as events]
   [practitest.frontend.login.subs :as subs]
   [practitest.frontend.components.icons :as icons]
   [practitest.frontend.components2.buttons :as buttons]
   [practitest.frontend.components2.info-note :refer [mux-info-note]]
   [practitest.frontend.components2.spinners :as spinners]
   [practitest.frontend.routing.router :as router]
   [practitest.frontend.common.i18n :refer [get-translation]]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]))

(defn- error-box [error]
  (when error
    [:div.error error]))

(defn- submit-button [text loading?]
  [:button.submit
   {:type     "submit"
    :disabled loading?}
   text
   (when loading?
     [spinners/mux-spinner {:size     :text
                            :color    :inverted
                            :fade-in? false}])])

(defn left-panel []
  (let [image-urls (re-frame/subscribe [::subs/login-image-urls])]
    (fn []
      [:div.left
       [:div.left-content
        [:h1.marketing-title (get-translation "login.left-pane.title" "Start Using PractiTest!")]
        [:p.marketing-subtitle
         (get-translation "login.left-pane.subtitle" "Combine test management leading methodologies and professional processes")]
        [:img.hero-image {:src (get @image-urls :bg "/assets/BG.png") :alt "PractiTest"}]]
       [:div.trust-badges
        [:img {:src "https://www.practitest.com/assets/img/compliance/SOC-loginpage.png"
               :alt "SOC2 Type 2"}]
        [:img {:src "https://www.practitest.com/assets/img/compliance/iso-login-resized.png"
               :alt "ISO 27001"}]
        [:img {:src (get @image-urls :aws_badge "/assets/AWS_TP.png") :alt "AWS Technology Partner"}]
        [:a {:href  "https://www.g2.com/products/practitest/review_modalities/new"
             :title "Write a review of PractiTest on G2"}
         [:img {:src (get @image-urls :g2_badge "/assets/G2-review-us-badge.png") :alt "G2"}]]]
       [:p.g2-review-text
        (get-translation "login.left-pane.g2-review" "Review us NOW on G2, send us a screenshot, and get a $25 gift card!")]])))

(defn- email-step []
  (let [email                (reagent/atom @(re-frame/subscribe [::subs/email]))
        error                (re-frame/subscribe [::subs/error])
        loading?             (re-frame/subscribe [::subs/loading?])
        cognito-enabled?     (re-frame/subscribe [::subs/cognito-enabled?])
        forgot-password-sent? (re-frame/subscribe [::subs/forgot-password-sent?])]
    (fn []
      [:div.right
       [:div.right-inner
        [:div.form-title (get-translation "login.title" "Login")]
        (when @forgot-password-sent?
          [mux-info-note {:message (get-translation "login.forgot-password.success-message" "If your account exists, a reset password link will be sent to your email address.")}])
        [:form.form
         {:on-submit (fn [e]
                       (.preventDefault e)
                       (re-frame/dispatch [::events/check-sso @email]))}
         [:div.field
          [:input#login-email.m-input
           {:type        "email"
            :placeholder (get-translation "login.email-placeholder" "test@practitest.com")
            :auto-focus  true
            :aria-label  (get-translation "generic.label.email" "Email")
            :disabled    @loading?
            :value       @email
            :on-change   #(reset! email (.. % -target -value))}]]
         [submit-button (get-translation "login.submit-button" "Log in") @loading?]
         [error-box @error]
         [:p.sso-note
          (get-translation "login.sso-note-prefix" "Note: If ")
          [:span.sso-note-keyword "SSO"]
          (get-translation "login.sso-note-suffix" " is configured, you'll be redirected automatically")]]
        (when @cognito-enabled?
          [:<>
           [:div.divider [:span.divider-label (get-translation "login.or-continue-with" "Or continue with")]]
           [:button.google-btn
            {:type       "button"
             :aria-label (get-translation "login.google-login-label" "Sign in with Google")
             :on-click   #(re-frame/dispatch [::events/google-login])}
            [icons/google-icon]]])
        [:p.signup
         (get-translation "login.signup-prompt" "Don't have an account? ")
         [buttons/button {:tertiary? true
                          :class     "signup-btn"
                          :text      (get-translation "login.signup-link" "Sign up")
                          :href      "https://www.practitest.com/free-trial/"}]]]])))

(defn- password-step []
  (let [email         @(re-frame/subscribe [::subs/email])
        password      (reagent/atom "")
        remember?      (reagent/atom false)
        show-password? (reagent/atom false)
        error         (re-frame/subscribe [::subs/error])
        loading?      (re-frame/subscribe [::subs/loading?])]
    (fn []
      [:div.right
       [:div.right-inner
        [:div.form-title (get-translation "login.title" "Login")]
        [:form.form
         {:on-submit (fn [e]
                       (.preventDefault e)
                       (re-frame/dispatch [::events/submit-login @password @remember?]))}
         [:div.field
          [:input#login-email-readonly.m-input
           {:type       "email"
            :value      email
            :aria-label (get-translation "generic.label.email" "Email")
            :disabled   true}]]
         [:div.field.field--password
          [:input#login-password.m-input
           {:type        (if @show-password? "text" "password")
            :placeholder (get-translation "login.password-placeholder" "Password")
            :auto-focus  true
            :aria-label  (get-translation "login.password-placeholder" "Password")
            :disabled    @loading?
            :value       @password
            :on-change   #(reset! password (.. % -target -value))}]
          [:button.eye-btn
           {:type       "button"
            :aria-label (if @show-password?
                          (get-translation "login.hide-password" "Hide password")
                          (get-translation "login.show-password" "Show password"))
            :on-click   #(swap! show-password? not)}
           (if @show-password? [icons/mux-eye-off] [icons/mux-eye])]]
         [:div.remember?
          [:label
           [:input
            {:type      "checkbox"
             :checked   @remember?
             :on-change #(swap! remember? not)}]
           " " (get-translation "login.remember-me" "Remember Me")]]
         [submit-button (get-translation "login.submit-button" "Log in") @loading?]
         [error-box @error]
         [:p.forgot
          (get-translation "login.no-project.sign-in-prompt" "Sign in as a different user")
          " "
          [buttons/button {:tertiary? true
                           :class     "forgot-btn"
                           :text      [:strong (get-translation "generic.label.click-here" "Click Here")]
                           :on-click  #(do (re-frame/dispatch [::events/set-email ""])
                                           (re-frame/dispatch [::router/replace-state :login]))}]]
         [:p.forgot
          (get-translation "login.forgot-password-prompt" "Forgot your password? ")
          [buttons/button {:tertiary? true
                           :class     "forgot-btn"
                           :text      [:strong (get-translation "generic.label.click-here" "Click Here")]
                           :on-click  #(re-frame/dispatch [::router/push-state :forgot-password])}]]]]])))

(defn- mfa-step []
  (let [code     (reagent/atom "")
        error    (re-frame/subscribe [::subs/error])
        loading? (re-frame/subscribe [::subs/loading?])]
    (fn []
      [:div.right
       [:div.right-inner
        [:div.form-title (get-translation "login.mfa.title" "Verification")]
        [:form.form
         {:on-submit (fn [e]
                       (.preventDefault e)
                       (re-frame/dispatch [::events/set-mfa-code @code])
                       (re-frame/dispatch [::events/submit-mfa]))}
         [:p.mfa-instructions
          (get-translation "login.mfa.instructions" "Enter the verification code sent to your device.")]
         [:div.field
          [:input#login-mfa-code.m-input
           {:type        "text"
            :placeholder (get-translation "login.mfa.code-placeholder" "6-digit code")
            :aria-label  (get-translation "login.mfa.code-placeholder" "6-digit code")
            :auto-focus  true
            :disabled    @loading?
            :max-length  6
            :value       @code
            :on-change   #(reset! code (.. % -target -value))}]]
         [submit-button (get-translation "login.mfa.submit-button" "Verify") @loading?]
         [error-box @error]]]])))

(defn login-page [_route]
  (fn [_route]
    (let [step @(re-frame/subscribe [::subs/step])]
      [:div.login-page
       [left-panel]
       (case step
         :email    [email-step]
         :password [password-step]
         :mfa      [mfa-step]
         [email-step])])))

(defn- option-link [label button-opts]
  [:p.login-forgot
   label
   [:span.forgot-suffix
    " - "
    [buttons/button (merge {:tertiary? true
                            :class     "forgot-btn"
                            :text      [:strong (get-translation "generic.label.click-here" "Click Here")]}
                           button-opts)]]])

(defn no-project-page [_route]
  (let [other-dcs @(re-frame/subscribe [::subs/other-datacenters])]
    [:div.login-page
     [left-panel]
     [:div.right
      [:div.right-inner
       [:div.form-title (get-translation "login.access-denied" "Access Denied")]
       [:p.forgot-password-description
        (get-translation "login.no-project.description-line1" "We couldn't find any projects associated with your email address.")
        [:br]
        (get-translation "login.no-project.description-line2" "To continue, please choose one of the following options:")]
       [option-link
        (get-translation "login.no-project.sign-in-prompt" "Sign in as a different user")
        {:on-click #(re-frame/dispatch [::router/replace-state :login])}]
       [option-link
        (get-translation "login.no-project.support-prompt" "Contact Support")
        {:href "mailto:support@practitest.com"}]
       (for [dc other-dcs]
         ^{:key dc}
         [option-link
          [:<> (get-translation "login.no-project.datacenter-prompt" "Login to") " " dc]
          {:href (str "https://" dc "/")}])]]]))

(re-frame/reg-sub
  ::user-without-project-data
  (fn [db _]
    (get-in db [:options :user-without-project-data])))

(defn user-without-project-page [_route]
  (let [{:keys [email has_expired_projects expired_project_names other_datacenters]}
        @(re-frame/subscribe [::user-without-project-data])]
    [:div.login-page
     [left-panel]
     [:div.right
      [:div.right-inner
       [:div.form-title
        (if has_expired_projects
          (get-translation "login.user-without-project.expired-page-title" "Project Expired")
          (get-translation "login.user-without-project.no-project-page-title" "No Projects Found"))]
       [:div.uwp-section
        [:p.forgot-password-description
         (get-translation "login.user-without-project.title" "Hi") " " [:strong email] ","]
        [:p.forgot-password-description
         (if has_expired_projects
           (get-translation "login.user-without-project.expired-title" "We are sorry, but your project expired.")
           (get-translation "login.user-without-project.no-project-title" "We are sorry, but there are no projects linked to your email."))]
        (when (and has_expired_projects expired_project_names)
          [:<>
           [:p.forgot-password-description.uwp-nowrap
            (get-translation "login.user-without-project.expired-projects-label"
                             "Here are the expired projects linked to your email address:")]
           [:p.forgot
            [:em expired_project_names]]])]
       [:hr.uwp-divider]
       [:div.uwp-section
        [:p.forgot-password-description.uwp-proceed
         (get-translation "login.user-without-project.proceed-prompt"
                          "To proceed please choose one of the following:")]
       [option-link
        (get-translation "login.user-without-project.sign-in-prompt" "Sign in as a different user")
        {:href "/logout"}]
       [option-link
        (if has_expired_projects
          (get-translation "login.user-without-project.support-expired-prompt" "Contact support to re-activate your project")
          (get-translation "login.no-project.support-prompt" "Contact Support"))
        {:href "mailto:support@practitest.com"}]
       (for [dc other_datacenters]
         ^{:key dc}
         [option-link
          [:<> (get-translation "login.no-project.datacenter-prompt" "Login to") " " dc]
          {:href (str "https://" dc "/")}])]]]]))


(defn forgot-password-page [_route]
  (let [email    (reagent/atom "")
        error    (re-frame/subscribe [::subs/error])
        loading? (re-frame/subscribe [::subs/loading?])]
    (fn [_route]
      [:div.login-page
       [left-panel]
       [:div.right
        [:div.right-inner
         [:div.form-title
          (get-translation "login.forgot-password.title" "Reset Password")]
         [:p.forgot-password-description
          (get-translation "login.forgot-password.description-line1"
                           "Please enter the email address you submitted with your account and press send.")
          [:br]
          (get-translation "login.forgot-password.description-line2"
                           "We will send you an email with a link, instructions, and a verification code (valid for 1 hour) to reset your password.")]
         [:form.form
          {:on-submit (fn [e]
                        (.preventDefault e)
                        (re-frame/dispatch [::events/submit-forgot-password @email]))}
          [:div.field
           [:input#forgot-email.m-input
            {:type        "email"
             :placeholder (get-translation "generic.label.email" "Email")
             :auto-focus  true
             :disabled    @loading?
             :value       @email
             :on-change   #(reset! email (.. % -target -value))}]]
          [submit-button (get-translation "generic.button.reset" "Reset") @loading?]
          [error-box @error]]]]])))
